import React from 'react';
import { Eye, EyeOff, Copy, Check } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';

const ApiKeysTab = ({
  showKey, setShowKey, copied, handleCopyKey,
  apiProviders, apiKeyStatus, apiKeys, setApiKeys,
  savingKey, handleSaveApiKey, handleRemoveApiKey
}) => {
  const inputClass = "bg-background border-input text-white placeholder:text-muted-foreground focus:border-blue-500/50";

  return (
    <div className="space-y-6">
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-foreground">SuperTool Universal AI Key</CardTitle>
          <CardDescription className="text-muted-foreground">One key for Claude Opus 4.6, Sonnet 4.6, GPT-5.2, and Grok</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl" data-testid="universal-key-status">
            <div className="flex items-center gap-2 mb-2"><div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" /><span className="text-emerald-400 font-semibold text-sm">Active</span></div>
            <p className="text-foreground/80 text-sm">Your Universal Key is active and working across all supported AI models.</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              <Input data-testid="api-key-display" type={showKey ? 'text' : 'password'} value="stk_live_supertool_universal_key" readOnly className={`${inputClass} pr-20 font-mono text-sm`} />
              <button onClick={() => setShowKey(!showKey)} className="absolute right-12 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">{showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
              <button data-testid="copy-key-btn" onClick={handleCopyKey} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">{copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}</button>
            </div>
          </div>
          <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
            <p className="text-blue-300 text-sm"><strong>Included models:</strong> Claude Opus 4.6, Claude Sonnet 4.6, Claude Sonnet 4, GPT-5.2, Grok (xAI)</p>
            <p className="text-muted-foreground text-xs mt-1">No need to manage separate API keys. Credits are shared across all models.</p>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-foreground">Custom API Keys (Optional)</CardTitle>
          <CardDescription className="text-muted-foreground">Use your own keys for higher rate limits</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {apiProviders.map((item) => (
            <div key={item.key} className="flex items-center gap-3 p-4 bg-muted rounded-lg border border-border" data-testid={`custom-key-${item.key}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium text-white text-sm">{item.name}</h4>
                  {apiKeyStatus[`has_${item.key}`] && <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full font-medium">Saved</span>}
                </div>
                <p className="text-xs text-muted-foreground">{item.desc}</p>
              </div>
              {apiKeyStatus[`has_${item.key}`] ? (
                <Button size="sm" variant="outline" onClick={() => handleRemoveApiKey(item.key)} disabled={savingKey === item.key} className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid={`remove-key-${item.key}`}>
                  {savingKey === item.key ? '...' : 'Remove'}
                </Button>
              ) : (
                <>
                  <Input placeholder={item.placeholder} value={apiKeys[item.key]} onChange={(e) => setApiKeys(prev => ({ ...prev, [item.key]: e.target.value }))} className={`w-64 ${inputClass} text-sm font-mono`} data-testid={`input-key-${item.key}`} />
                  <Button size="sm" variant="outline" onClick={() => handleSaveApiKey(item.key)} disabled={savingKey === item.key} className="border-input text-foreground/80 hover:bg-accent" data-testid={`save-key-${item.key}`}>
                    {savingKey === item.key ? '...' : 'Save'}
                  </Button>
                </>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default ApiKeysTab;
